import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useNotifications } from '@toolpad/core';
import {
    CheckboxElement,
    FormContainer,
    TextFieldElement,
    useForm,
} from 'react-hook-form-mui';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
} from '@mui/material';

import { ProductCategoryInput } from '@barboxx/admin-components';
import { CREATE_ALCOHOL_TYPE_SCHEMA } from '@barboxx/admin-utils';
import Api from '@barboxx/api';
import { ProductType } from '@barboxx/models';

interface Props {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

export const CreateProductTypeDialog = ({
    onClose,
    onSuccess,
    open,
}: Props) => {
    const notifications = useNotifications();

    const formContext = useForm<ProductType.CreateRequest>({
        resolver: yupResolver(CREATE_ALCOHOL_TYPE_SCHEMA),
        defaultValues: {},
    });

    const mutateCreateProductType = useMutation({
        mutationKey: ['createProductType'],
        mutationFn: (data: ProductType.CreateRequest) =>
            Api.Products.Types.postProductType(data),
        onSuccess: () => {
            formContext.reset();
        },
    });

    const onSubmit = async (values: ProductType.CreateRequest) => {
        return mutateCreateProductType
            .mutateAsync(values)
            .then(({ data }) => {
                notifications.show(`Created ${values.name}!`, {
                    severity: 'success',
                });
                onClose();
                onSuccess();
            })
            .catch(e => {
                notifications.show('Error creating product!', {
                    severity: 'error',
                });
            });
    };

    const handleCancelCreate = () => {
        formContext.reset();
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            fullWidth
        >
            <DialogTitle id="alert-dialog-title">
                Create Alcohol Type
            </DialogTitle>
            <FormContainer onSuccess={onSubmit} formContext={formContext}>
                <DialogContent
                    sx={{ gap: 2, display: 'flex', flexDirection: 'column' }}
                >
                    <TextFieldElement label="Name" name="name" fullWidth />
                    <TextFieldElement
                        label="Description"
                        name="description"
                        fullWidth
                    />
                    <TextFieldElement
                        label="ABV Range"
                        name="abvRange"
                        fullWidth
                    />
                    <TextFieldElement
                        label="Production Method"
                        name="productionMethod"
                        fullWidth
                    />
                    <ProductCategoryInput />
                    <CheckboxElement name="isAlcoholic" label="Is Alcoholic" />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelCreate} color="inherit">
                        Cancel
                    </Button>

                    <Button
                        color="primary"
                        variant="contained"
                        loading={formContext.formState.isSubmitting}
                        type="submit"
                        disabled={formContext.formState.isSubmitting}
                    >
                        Create
                    </Button>
                </DialogActions>
            </FormContainer>
        </Dialog>
    );
};
